<script lang="ts">
  import { page } from '$app/stores';
  import { cx } from '@emotion/css';
  import { fade } from 'svelte/transition';
  import DropdownMenu from '$components/Dropdown/DropdownMenu.svelte';
  import type { NavItem as NavItemType } from '$lib/sanity/schemas';

  import NavItem from '$components/Navigation/NavItem.svelte';
  import { TwistStack, ResponsiveVars } from '@twist/core';
  import { toFluidVarInput } from '$lib/utils/fluidity';
  import { getDropdownItemsByParent, resolveHref } from './GlobalNav.svelte';
  import ChevronDown from '$components/Icons/ChevronDown.svelte';
  import { flagValue } from '$lib/context/flags';
  import { FEATURE_FLAGS } from '$lib/flags/constants';
  import Checkmark from '$components/Icons/Checkmark.svelte';
  import { MiscTypes } from '$lib/sanity/types';

  interface Props {
    navItems?: NavItemType[];
    activeItem?: NavItemType | undefined;
    handleDropdown: (item: NavItemType) => void;
    navbarHeight?: number;
    dropdownHeight?: number;
    mobileNavOpen?: boolean;
  }
  let {
    navItems = [],
    activeItem = undefined,
    handleDropdown,
    navbarHeight = 74,
    dropdownHeight = $bindable(),
    mobileNavOpen = false
  }: Props = $props();

  const dropdownItemsByParent = getDropdownItemsByParent(navItems);

  let brandReskinEnabled = $derived(flagValue(FEATURE_FLAGS.BRAND_RESKIN));

  const responsiveVars = new ResponsiveVars()
    .addFluid('nav-items-gap', toFluidVarInput(9, 9))
    .addFluid('nav-items-gap-reskin', toFluidVarInput(9, 18))
    .toCSS();
</script>

<ul class={cx('nav-items', responsiveVars, { reskin: brandReskinEnabled })}>
  {#each navItems as item}
    {#if item.itemType === 'folder' && item.folder}
      <li>
        <NavItem
          as="button"
          text={item.folder.name || ''}
          name={item.folder.name}
          type="button"
          ariaControls={`${item.folder.name}-dropdown-menu`}
          ariaExpanded={activeItem?.id === item.id}
          onClick={() => handleDropdown(item)}
        >
          {#snippet children()}
            <span class="dropdown-icon" class:dropdown-icon--active={activeItem?.id === item.id}>
              <ChevronDown />
            </span>
          {/snippet}
        </NavItem>
      </li>
      {#if !mobileNavOpen && activeItem?.id === item.id}
        <div
          transition:fade={{ duration: 100 }}
          class="navigation-dropdown"
          class:expanded={activeItem?.id === item.id}
          style={`--top: ${navbarHeight}px;`}
        >
          <DropdownMenu
            label={activeItem?.folder?.name}
            bind:height={dropdownHeight}
            expanded={activeItem?.id === item.id}
          >
            <ul class="dropdown-items">
              <TwistStack
                direction="column"
                justify="flex-start"
                align="normal"
                spacing={toFluidVarInput(10, 9)}
              >
                {#each dropdownItemsByParent[item.id] as ddItem, i}
                  {#if ddItem.link}
                    <li
                      class="dropdown-item-wrapper"
                      class:last={i === dropdownItemsByParent[item.id].length - 1}
                    >
                      <NavItem
                        text={ddItem.link.title || ''}
                        href={resolveHref(ddItem) || '#'}
                        tabIndex={resolveHref(ddItem) === $page.url.pathname ? -1 : 0}
                        data-sveltekit-reload={ddItem.link.destination.page?._type ===
                        MiscTypes.Document
                          ? 'true'
                          : undefined}
                      >
                        {#snippet icon()}
                          <span class="checkmark">
                            {#if resolveHref(ddItem) === $page.url.pathname}
                              <Checkmark />
                            {/if}
                          </span>
                        {/snippet}
                      </NavItem>
                    </li>
                  {/if}
                {/each}
              </TwistStack>
            </ul>
          </DropdownMenu>
        </div>
      {/if}
    {:else if item.itemType === 'link' && !item.parent && item.link}
      <li>
        <NavItem
          text={item.link?.title || ''}
          href={resolveHref(item) || '#'}
          data-sveltekit-reload={item.link.destination.page?._type === MiscTypes.Document
            ? 'true'
            : undefined}
        />
      </li>
    {/if}
  {/each}
</ul>

<style lang="scss">
  @import 'src/scss/media';

  .nav-items {
    display: none;

    @include at(lg) {
      display: flex;
      align-items: center;
      gap: var(--nav-items-gap);
    }
  }

  .nav-items.reskin {
    @include at(lg) {
      gap: var(--nav-items-gap-reskin);
    }
  }

  :global(.checkmark) {
    display: inline-flex;
    align-items: center;
  }

  .navigation-dropdown {
    display: none;

    @include at(lg) {
      display: block;
      position: absolute;
      top: var(--top, 74);
      left: 0;
      width: 100%;
    }
  }
</style>
